import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, PageSpinner } from '@/components/common';
import { ROLE_CSS_CLASS, type AdminUser } from '@/data/mockUsers';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, useToggleUserActive, useUpdateUserRoles, useUpdateUserPermissions } from '@/hooks/useUsers';
import { useOUs, useOUItems } from '@/hooks/useOUs';
import { OuTreeSelect } from '@/components/common/OuTree';
import { useRules, useUpdateRule } from '@/hooks/useRules';
import { useUserContext } from '@/contexts/UserContext';

/** Maps frontend i18n role key → backend UserRole enum string */
const ROLE_KEY_TO_ENUM: Record<string, string> = {
  'admin.roleAdminCentral': 'AdminGlobal',
  'admin.roleAdminOU': 'AdminOU',
};

const PAGE_SIZE = 5;

interface FormState {
  open: boolean;
  editId: string | null;
  email: string;
  role: string;
  ou: string;
}

const FORM_DEFAULT: FormState = { open: false, editId: null, email: '', role: '', ou: '' };

export function UsersTab() {
  const { t } = useTranslation();
  const { demoUser } = useUserContext();
  const { data: rulesConfig } = useRules();
  const updateRule = useUpdateRule();
  // OU toggle — driven by backend rule (falls back to false while loading)
  const [ouCreateSurveys, setOuCreateSurveys] = useState<boolean>(false);
  const [localUsers, setLocalUsers] = useState<AdminUser[] | null>(null);
  const [form, setForm] = useState<FormState>(FORM_DEFAULT);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [emailError, setEmailError] = useState(false);
  const [page, setPage] = useState(1);

  const { data: fetchedUsers = [], isLoading: loadingUsers } = useUsers();
  const { data: ous = [], isLoading: loadingOUs } = useOUs();
  const { data: ouItems = [] } = useOUItems();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const toggleActive = useToggleUserActive();
  const updateRoles = useUpdateUserRoles();
  const updatePermissions = useUpdateUserPermissions();

  // Hydrate OU toggle from backend rule on first load
  useEffect(() => {
    if (rulesConfig) setOuCreateSurveys(rulesConfig.ouAdminsCanCreate);
  }, [rulesConfig]);

  // Use localUsers override (optimistic UI) or fetched data
  const users: AdminUser[] = localUsers ?? fetchedUsers;

  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  const paginated = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function openForm(user?: AdminUser) {
    if (user) {
      setForm({ open: true, editId: user.id, email: user.email, role: user.roleKey, ou: user.ouId ?? '' });
    } else {
      setForm({ ...FORM_DEFAULT, open: true });
    }
  }

  function closeForm() { setForm(FORM_DEFAULT); setEmailError(false); }

  function saveForm() {
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!form.email || !EMAIL_RE.test(form.email)) { setEmailError(true); return; }
    setEmailError(false);
    if (!form.role) return;
    if (form.editId) {
      const isCentral = form.role === 'admin.roleAdminCentral';
      const ouLabel = form.ou && form.ou !== 'todas' ? (ous.find(o => o.key === form.ou)?.label ?? form.ou) : 'Todas';
      const alcance = isCentral ? 'Global' : (form.ou && form.ou !== 'todas' ? ouLabel : 'Todas');
      const prev = localUsers ?? users;
      setLocalUsers(prev.map(u => u.id === form.editId
        ? { ...u, email: form.email, roleKey: form.role, ouId: isCentral ? '' : (form.ou || u.ouId), ou: isCentral ? 'Todas' : ouLabel, alcance }
        : u
      ));
      updateRoles.mutate(
        { id: form.editId, roles: [ROLE_KEY_TO_ENUM[form.role] ?? form.role] },
        { onError: () => setLocalUsers(prev) },
      );
      if (form.ou) {
        updateUser.mutate(
          { id: form.editId, body: { ouId: form.ou } },
          { onError: () => setLocalUsers(prev) },
        );
      }
    } else {
      // No optimistic insert — wait for real ID from server to avoid
      // toggles firing with a fake Date.now() id that the backend rejects.
      createUser.mutate(
        {
          email: form.email,
          displayName: form.email.split('@')[0],
          roles: [ROLE_KEY_TO_ENUM[form.role] ?? form.role],
          ouId: form.ou || undefined,
        },
        { onSuccess: () => setLocalUsers(null) },
      );
    }
    closeForm();
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    setLocalUsers(us => (us ?? users).filter(u => u.id !== deleteTarget.id));
    deleteUser.mutate(deleteTarget.id);
    setDeleteTarget(null);
  }

  function toggleUserActive(id: string) {
    const prev = localUsers ?? users;
    const user = prev.find(u => u.id === id);
    if (!user) return;
    setLocalUsers(prev.map(u => u.id === id ? { ...u, active: !u.active } : u));
    toggleActive.mutate(
      { id, isActive: !user.active },
      { onError: () => setLocalUsers(prev) },
    );
  }

  function togglePermission(id: string, perm: 'canCreateSurveys' | 'canViewAnalytics' | 'canManageUsers' | 'canApproveSurveys') {
    const prev = localUsers ?? users;
    const updated = prev.map(u => u.id === id ? { ...u, [perm]: !u[perm] } : u);
    setLocalUsers(updated);
    const user = updated.find(u => u.id === id);
    if (user) {
      updatePermissions.mutate(
        {
          id,
          perms: {
            canCreateSurveys:  user.canCreateSurveys,
            canViewAnalytics:  user.canViewAnalytics,
            canManageUsers:    user.canManageUsers,
            canApproveSurveys: user.canApproveSurveys,
          },
        },
        { onError: () => setLocalUsers(prev) },
      );
    }
  }

  function changeRole(id: string, roleKey: string) {
    const prev = localUsers ?? users;
    const isCentral = roleKey === 'admin.roleAdminCentral';
    setLocalUsers(prev.map(u => {
      if (u.id !== id) return u;
      if (isCentral) return { ...u, roleKey, ouId: '', ou: 'Todas', alcance: 'Global' };
      return { ...u, roleKey };
    }));
    updateRoles.mutate(
      { id, roles: [ROLE_KEY_TO_ENUM[roleKey] ?? roleKey] },
      { onError: () => setLocalUsers(prev) },
    );
  }

  return (
    <div className="adm-tab-content">
      {deleteTarget && (
        <ConfirmModal
          type="danger"
          title={t('admin.userDeleteTitle')}
          message={t('admin.userDeleteDesc', { name: deleteTarget.name })}
          confirmText={t('common.delete')}
          onConfirm={confirmDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      <div className="admin-header">
        <h2>{t('admin.usersAdminTitle')}</h2>
        <button className="btn btn-primary btn-small" onClick={() => openForm()}>
          <i className="fas fa-plus" /> {t('admin.addAdmin')}
        </button>
      </div>

      {/* Add/Edit Admin Form */}
      {form.open && (
        <div className="add-admin-form">
          <div className="form-card">
            <div className="form-card-header">
              <h3><i className="fas fa-user-plus" /> {form.editId ? t('admin.editAdmin') : t('admin.newAdmin')}</h3>
              <button className="btn-close" onClick={closeForm}>×</button>
            </div>
            <div className="form-card-body">
              <div className="form-group">
                <label>{t('admin.adminEmail')} *</label>
                <input
                  type="email"
                  className={`form-input${emailError ? ' input-error' : ''}`}
                  placeholder="nombre@sigma-alimentos.com"
                  value={form.email}
                  onChange={e => { setEmailError(false); setForm(f => ({ ...f, email: e.target.value })); }}
                />
                {emailError && <span className="field-error">{t('admin.adminEmailInvalid')}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="admin-role-select">{t('admin.adminRole')} *</label>
                <select id="admin-role-select" className="form-input" value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value, ou: e.target.value === 'admin.roleAdminCentral' ? '' : f.ou }))}>
                  <option value="">{t('admin.adminRolePlaceholder')}</option>
                  <option value="admin.roleAdminCentral">{t('admin.roleAdminCentral')}</option>
                  <option value="admin.roleAdminOU">{t('admin.roleAdminOU')}</option>
                </select>
              </div>
              {form.role === 'admin.roleAdminOU' && (
                <div className="form-group">
                  <label>{t('admin.adminOU')} *</label>
                  <button
                    type="button"
                    className={`ou-scope-all-btn${form.ou === 'todas' ? ' active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, ou: f.ou === 'todas' ? '' : 'todas' }))}
                  >
                    {t('admin.adminOUAll')}
                  </button>
                  <OuTreeSelect
                    mode="single"
                    variant="dropdown"
                    items={ouItems}
                    value={form.ou && form.ou !== 'todas' ? [form.ou] : []}
                    onChange={ids => setForm(f => ({ ...f, ou: ids[0] ?? '' }))}
                    placeholder={t('admin.adminOUPlaceholder')}
                    isDisabled={form.ou === 'todas'}
                  />
                </div>
              )}
              <div className="form-actions">
                <button className="btn btn-secondary" onClick={closeForm}>{t('common.cancel')}</button>
                <button className="btn btn-primary" onClick={saveForm}
                  disabled={createUser.isPending || updateUser.isPending || updateRoles.isPending}>
                  {(createUser.isPending || updateUser.isPending || updateRoles.isPending)
                    ? <i className="fas fa-spinner fa-spin" />
                    : <i className="fas fa-save" />
                  }
                  {' '}{t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global OU permission card — solo visible para AdminGlobal */}
      {demoUser.role === 'AdminGlobal' && <div className="perm-global-card">
        <div className="perm-global-left">
          <div className="perm-global-icon"><i className="fas fa-building" /></div>
          <div>
            <div className="perm-global-title">{t('admin.permGlobalTitle')}</div>
            <div className="perm-global-desc">{t('admin.permGlobalDesc')}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="perm-global-status" style={{ color: ouCreateSurveys ? 'var(--cp-green)' : 'var(--gray-400)' }}>
            {ouCreateSurveys ? t('admin.permEnabled') : t('admin.permDisabled')}
          </span>
          <div
            className={`big-toggle${ouCreateSurveys ? ' on' : ''}`}
            onClick={() => {
              const next = !ouCreateSurveys;
              setOuCreateSurveys(next);
              updateRule.mutate({
                code: 'OU_ADMINS_CREATE_SURVEYS',
                body: { description: 'OU admins can create surveys', value: String(next), isActive: true },
              });
            }}
            role="switch"
            aria-checked={ouCreateSurveys}
          >
            <div className="big-toggle-knob" />
          </div>
        </div>
      </div>}

      {(loadingUsers || loadingOUs) && <PageSpinner />}
      <div className="admins-table-container" style={{ display: (loadingUsers || loadingOUs) ? 'none' : undefined }}>
        <table className="admins-table">
          <thead>
            <tr>
              <th>{t('admin.colUser')}</th>
              <th>{t('admin.roles')}</th>
              <th>{t('admin.colAlcance')}</th>
              <th>{t('admin.colPermisos')}</th>
              <th>{t('admin.colEstado')}</th>
              <th>{t('admin.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(user => (
              <tr key={user.id}>
                {/* Usuario */}
                <td>
                  <div className="adm-user-cell">
                    <div className="adm-user-avatar">
                      {user.name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{user.name}</div>
                      <div className="adm-text-muted" style={{ fontSize: '0.75rem' }}>{user.email}</div>
                    </div>
                  </div>
                </td>

                {/* Rol — selector inline */}
                <td>
                  <select
                    className={`role-select ${ROLE_CSS_CLASS[user.roleKey] ?? ''}`}
                    value={user.roleKey}
                    onChange={e => changeRole(user.id, e.target.value)}
                  >
                    <option value="admin.roleAdminCentral">{t('admin.roleAdminCentral')}</option>
                    <option value="admin.roleAdminOU">{t('admin.roleAdminOU')}</option>
                  </select>
                </td>

                {/* Alcance */}
                <td className="adm-text-muted">{user.alcance}</td>

                {/* Permisos — 4 toggles */}
                <td>
                  <div className="perm-toggles-list">
                    {([
                      ['canCreateSurveys',  'admin.permCreateSurveys' ],
                      ['canViewAnalytics',  'admin.permViewAnalytics' ],
                      ['canManageUsers',    'admin.permManageUsers'   ],
                      ['canApproveSurveys', 'admin.permApproveSurveys'],
                    ] as const).map(([perm, labelKey]) => (
                      <div key={perm} className="perm-toggle-row">
                        <label className="switch switch-sm">
                          <input type="checkbox" checked={user[perm]}
                            onChange={() => togglePermission(user.id, perm)} />
                          <span className="slider" />
                        </label>
                        <span className="perm-toggle-label">{t(labelKey)}</span>
                      </div>
                    ))}
                  </div>
                </td>

                {/* Estado */}
                <td>
                  <label className="switch switch-sm">
                    <input type="checkbox" checked={user.active}
                      onChange={() => toggleUserActive(user.id)} />
                    <span className="slider" />
                  </label>
                </td>

                {/* Acciones */}
                <td>
                  <div className="adm-actions">
                    <button className="btn-icon" title={t('common.edit')} onClick={() => openForm(user)}>
                      <i className="fas fa-pen" />
                    </button>
                    <button className="btn-icon danger" title={t('common.delete')} onClick={() => setDeleteTarget(user)}>
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--gray-400)' }}>
                  {t('admin.noUsersFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pager-wrap">
            <span className="pager-info">{users.length} {t('admin.adminCount')}</span>
            <div className="pager-controls">
              <button className="pager-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <i className="fas fa-chevron-left" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`pager-btn${page === p ? ' active' : ''}`} onClick={() => setPage(p)}>
                  {p}
                </button>
              ))}
              <button className="pager-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
